"use client";

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import PageHeader from "@/components/shared/PageHeader";
import GlassPanel from "@/components/dashboard/GlassPanel";
import Footer from "@/components/layout/Footer";
import { CourseList } from "@/components/courses/CourseList";
import { getPublicOfferings } from "@/lib/api/institute";

const PAGE_SIZE = 20;
const MAX_FETCH_PAGES = 10;

export default function PublicCoursesPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { data: allOfferings = [], isLoading, error, refetch } = useQuery({
    queryKey: ["public-offerings-all"],
    queryFn: async () => {
      const first = await getPublicOfferings({ page: 1, pageSize: 100 });
      const results = [...first.results];
      const totalPages = first.totalPages;
      for (let p = 2; p <= Math.min(totalPages, MAX_FETCH_PAGES); p++) {
        const pageData = await getPublicOfferings({ page: p, pageSize: 100 });
        results.push(...pageData.results);
      }
      return results;
    },
    staleTime: 1000 * 60 * 5,
  });

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allOfferings;
    return allOfferings.filter((offering) => {
      const text = [
        offering.title,
        offering.course.courseName,
        offering.course.category,
        offering.description,
        offering.deliveryMode,
        offering.institute.instituteName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return text.includes(term);
    });
  }, [allOfferings, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, safePage]);

  return (
    <>
      <div className="space-y-6">
        <PageHeader
          title="Browse Courses"
          subtitle="Explore published maritime training courses from approved institutes"
        />

        <GlassPanel className="p-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
            <input
              type="text"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Search by course, institute, category, or delivery mode"
              className="h-12 w-full rounded-xl border border-neon-cyan/15 bg-background pl-10 pr-4 text-sm text-foreground outline-none transition focus:border-neon-cyan/60"
            />
          </div>
        </GlassPanel>

        <CourseList
          offerings={paginated}
          isLoading={isLoading}
          error={error as Error | null}
          onRefetch={refetch}
          count={filtered.length}
          page={safePage}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
      <Footer />
    </>
  );
}
